import React, { useState, useMemo, useEffect } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { TourProvider } from '@reactour/tour';
import { useTranslatedSteps } from './stepsTour';
import VideoProvider from '../components/VideoProvider';
import { videoUrls, videoTitles } from '../utils/videoUrls';
import DocumentManager from './DocumentManager';
import { AutomateResponseContent } from './automatePage/automateResponse';
import Connections from './Connections';
import { BehaviorContent } from './Behavior';
import { ChannelContent } from './Channel';
import { AdvancedSettingsContent } from './AdvancedSettings';
import { useTranslation } from '../contexts/TranslationContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`aida-settings-tabpanel-${index}`}
      aria-labelledby={`aida-settings-tab-${index}`}
      style={{ width: '100%', height: '100%', display: value === index ? 'block' : 'none' }}
    >
      {value === index && <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>{children}</Box>}
    </div>
  );
};

const AIDA_SETTINGS_CONNECTIONS_TAB_INDEX = 3;

const AidaSettings: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialTab = useMemo(() => {
    const tabParam = searchParams.get('tab');
    const n = parseInt(tabParam ?? '', 10);
    if (Number.isNaN(n) || n < 0 || n > 5) return 0;
    return n;
  }, [searchParams]);
  const [value, setValue] = useState(initialTab);
  const { t } = useTranslation();
  const connectionsSubTabParam = searchParams.get('subtab');
  const connectionsSubTab =
    connectionsSubTabParam === '1' || connectionsSubTabParam === '2'
      ? Number(connectionsSubTabParam)
      : undefined;

  useEffect(() => {
    setValue(initialTab);
  }, [initialTab]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const steps = [
    ...useTranslatedSteps().steps_behavior,
    ...useTranslatedSteps().steps_channel,
    ...useTranslatedSteps().steps_autoResponse,
  ];

  return (
    <TourProvider steps={steps}>
      <VideoProvider videoUrl={videoUrls.aidaSetting} videoTitle={videoTitles.aidaSetting}>
        <Box sx={{ width: '100%', height: '90vh', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '15px',
              px: { xs: 1, sm: 2 },
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="aida settings tabs"
              sx={{
                width: '100%',
                maxWidth: 'max-content',
                border: '1.5px solid black',
                borderBottom: 'none',
                borderTopRightRadius: '16px',
                borderTopLeftRadius: '16px',
                '& .MuiTabs-flexContainer': {
                  justifyContent: 'center',
                },
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTab-root': {
                  backgroundColor: '#D9D9D980',
                  color: '#000',
                  fontWeight: 400,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  minWidth: 120,
                  flex: '1 1 0',
                  textAlign: 'center',
                  fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                  py: { xs: 0.5, sm: 1 },
                  px: { xs: 0.5, sm: 1.5 },
                },
                '& .MuiTab-root:first-of-type': {
                  borderTopLeftRadius: '15px',
                },
                '& .MuiTab-root:last-of-type': {
                  borderTopRightRadius: '15px',
                },
                '& .MuiTab-root:not(:last-of-type)': {
                  borderRight: '1.5px solid black',
                },
                '& .MuiTab-root.Mui-selected': {
                  backgroundColor: '#009688',
                  color: '#fff',
                  fontWeight: 700,
                },
                '@media (max-width:1040px)': {
                  '& .MuiTabs-flexContainer': {
                    flexWrap: 'wrap',
                  },
                  '& .MuiTab-root': {
                    minWidth: 0,
                    flex: '0 0 33.3333%',
                    maxWidth: '33.3333%',
                  },
                  '& .MuiTab-root:nth-of-type(n+4)': {
                    borderTop: '1.5px solid black',
                  },
                  '& .MuiTab-root:nth-of-type(3)': {
                    borderTopRightRadius: '15px',
                    borderRight: 'none',
                  },
                  '& .MuiTab-root:last-of-type': {
                    borderTopRightRadius: 0,
                  },
                },
              }}
            >
              <Tab label={t('aidaSettings.tabs.tab1')} id="aida-settings-tab-0" aria-controls="aida-settings-tabpanel-0" />
              <Tab label={t('aidaSettings.tabs.tab2')} id="aida-settings-tab-1" aria-controls="aida-settings-tabpanel-1" />
              <Tab label={t('aidaSettings.tabs.tab3')} id="aida-settings-tab-2" aria-controls="aida-settings-tabpanel-2" />
              <Tab label={t('aidaSettings.tabs.tab4')} id="aida-settings-tab-3" aria-controls="aida-settings-tabpanel-3" />
              <Tab label={t('aidaSettings.tabs.tab5')} id="aida-settings-tab-4" aria-controls="aida-settings-tabpanel-4" />
              <Tab label={t('aidaSettings.tabs.tab6')} id="aida-settings-tab-5" aria-controls="aida-settings-tabpanel-5" />
            </Tabs>
          </Box>
          <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0, height: '100%' , border: '1.5px solid black',borderRadius: '20px', padding: {xs: '1px', md: '10px'}, margin: '0 15px'}}>
            <TabPanel value={value} index={0}>
              <DocumentManager />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <AdvancedSettingsContent />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <AutomateResponseContent />
            </TabPanel>
            <TabPanel value={value} index={AIDA_SETTINGS_CONNECTIONS_TAB_INDEX}>
              <Connections initialSubTab={connectionsSubTab} />
            </TabPanel>
            <TabPanel value={value} index={4}>
              <BehaviorContent />
            </TabPanel>
            <TabPanel value={value} index={5}>
              <ChannelContent />
            </TabPanel>
          </Box>
        </Box>
      </VideoProvider>
    </TourProvider>
  );
};

export default AidaSettings;

